import {
  Form,
  Input,
  Button,
  DatePicker,
  TimePicker,
  Divider,
  Switch,
  Row,
  Col,
} from "antd";
import { Plus, Trash2 } from "lucide-react";
import dayjs from "dayjs";
import MediaUpload from "Components/MediaUpload/MediaUpload";
import EventItemSelector from "./_blocks/EventItemSelector";
import { textInputValidator } from "commons/helper";

const MAX_LIST_ITEMS = 5;

const disableFutureDates = (current, info) =>
  info?.type === "date" ? current && current > dayjs().endOf("day") : false;

const disablePastDates = (current, info) =>
  info?.type === "date" ? current && current < dayjs().startOf("day") : false;

function Step3Content({
  amenities,
  activities,
  loadingData,
  form,
  showTC,
  setShowTC,
}) {
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Content & Extras</h2>

      {/* Amenities */}
      <Divider plain>
        <span className="text-sm font-semibold">Amenities</span>
      </Divider>
      <Form.Item
        name="amenities"
        rules={[
          {
            required: true,
            validator(_, val) {
              return (val ?? []).length < 1
                ? Promise.reject(new Error("Add at least one amenity."))
                : Promise.resolve();
            },
          },
        ]}
      >
        <EventItemSelector
          masterItems={amenities}
          nameKey="amenity_name"
          urlKey="icon_url"
          loading={loadingData}
          placeholder="Add an amenity…"
          showDescription={false}
        />
      </Form.Item>

      {/* Activities */}
      <Divider plain>
        <span className="text-sm font-semibold">Activities / Stages</span>
      </Divider>
      <Form.Item
        name="activities"
        rules={[
          {
            required: true,
            validator(_, val) {
              if ((val ?? []).length < 1)
                return Promise.reject(new Error("Add at least one activity."));
              const missing = val.find((v) => !v.description?.trim());
              return missing
                ? Promise.reject(
                    new Error(`Add a description for "${missing.name}".`)
                  )
                : Promise.resolve();
            },
          },
        ]}
      >
        <EventItemSelector
          masterItems={activities}
          nameKey="activity_name"
          urlKey="media_url"
          loading={loadingData}
          placeholder="Add an activity…"
          showDescription={true}
        />
      </Form.Item>

      {/* Organiser Details */}
      <Divider plain>
        <span className="text-sm font-semibold">Organiser Details</span>
      </Divider>
      <div className="rounded-xl border p-4 bg-muted/20 space-y-3">
        <Row gutter={[16, 12]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="organiser_name"
              label="Organiser Name"
              className="mb-0"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: "Organiser name is required",
                },
                { validator: textInputValidator },
              ]}
            >
              <Input
                placeholder="e.g. Showup Events Pvt Ltd"
                maxLength={50}
                showCount
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="organiser_total_events"
              label="Total Events Hosted"
              className="mb-0"
            >
              <Input
                type="number"
                min={0}
                maxLength={7}
                placeholder="e.g. 10"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="organiser_since"
              label="Hosting Since"
              className="mb-0"
            >
              <DatePicker
                placeholder="2020-01-15"
                style={{ width: "100%" }}
                format="YYYY-MM-DD"
                disabledDate={disableFutureDates}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="organiser_logo_url"
              label="Logo (1:1, min 400×400px, Max 2MB)"
              rules={[
                { required: true, message: "Organiser logo is required" },
              ]}
            >
              <MediaUpload listType="picture-circle" maxSizeMB={2} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* FAQ */}
      <Divider plain>
        <span className="text-sm font-semibold">FAQ (Max 5)</span>
      </Divider>
      <Form.List name="faq">
        {(fields, { add, remove }) => (
          <div className="space-y-3">
            {fields.map(({ key, name }) => (
              <div
                key={key}
                className="rounded-xl border p-4 bg-muted/20 space-y-2"
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">FAQ {name + 1}</span>
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => remove(name)}
                  />
                </div>
                <Form.Item
                  name={[name, "question"]}
                  label="Question"
                  rules={[
                    { required: true, whitespace: true },
                    { validator: textInputValidator },
                  ]}
                >
                  <Input
                    placeholder="e.g. Is there an age restriction?"
                    maxLength={200}
                    showCount
                  />
                </Form.Item>
                <Form.Item
                  name={[name, "answer"]}
                  label="Answer"
                  rules={[
                    { required: true, whitespace: true },
                    { validator: textInputValidator },
                  ]}
                >
                  <Input.TextArea rows={2} maxLength={500} showCount />
                </Form.Item>
              </div>
            ))}
            {fields.length < MAX_LIST_ITEMS && (
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<Plus className="h-4 w-4" />}
              >
                Add FAQ
              </Button>
            )}
          </div>
        )}
      </Form.List>

      {/* Things to Know */}
      <Divider plain>
        <span className="text-sm font-semibold">Things to Know (Max 5)</span>
      </Divider>
      <Form.List name="things_to_know">
        {(fields, { add, remove }) => (
          <div className="space-y-2">
            {fields.map(({ key, name }) => (
              <div key={key} className="flex items-center gap-2">
                <Form.Item
                  name={name}
                  noStyle
                  rules={[
                    {
                      required: true,
                      message: "Item is required",
                      whitespace: true,
                    },
                    { validator: textInputValidator },
                  ]}
                >
                  <Input
                    placeholder="e.g. Carry a valid government-issued photo ID"
                    maxLength={100}
                    showCount
                  />
                </Form.Item>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={() => remove(name)}
                />
              </div>
            ))}
            {fields.length < MAX_LIST_ITEMS && (
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<Plus className="h-4 w-4" />}
              >
                Add Item
              </Button>
            )}
          </div>
        )}
      </Form.List>

      {/* Partners */}
      <Divider plain>
        <span className="text-sm font-semibold">Partners (Max 5)</span>
      </Divider>
      <Form.List name="partners">
        {(fields, { add, remove }) => (
          <div className="space-y-3">
            {fields.map(({ key, name }) => (
              <div key={key} className="rounded-xl border p-3 bg-muted/20">
                <Row gutter={[16, 12]} align="bottom">
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name={[name, "name"]}
                      label="Partner Name"
                      className="mb-0"
                      rules={[
                        { required: true, whitespace: true },
                        { validator: textInputValidator },
                      ]}
                    >
                      <Input
                        placeholder="e.g. Red Bull India"
                        maxLength={50}
                        showCount
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={20} sm={10}>
                    <Form.Item
                      name={[name, "logo_url"]}
                      label="Logo"
                      className="mb-0"
                      rules={[
                        { required: true, message: "Partner logo is required" },
                      ]}
                    >
                      <MediaUpload listType="picture-circle" />
                    </Form.Item>
                  </Col>
                  <Col xs={4} sm={2}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<Trash2 className="h-4 w-4" />}
                      onClick={() => remove(name)}
                      className="mb-1"
                    />
                  </Col>
                </Row>
              </div>
            ))}
            {fields.length < MAX_LIST_ITEMS && (
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<Plus className="h-4 w-4" />}
              >
                Add Partner
              </Button>
            )}
          </div>
        )}
      </Form.List>

      {/* Schedule */}
      <Divider plain>
        <span className="text-sm font-semibold">Schedule (Max 15)</span>
      </Divider>
      <Form.Item
        name="schedule_info_validator"
        rules={[
          {
            validator() {
              const items = form.getFieldValue("schedule_info") ?? [];
              const filled = items.filter(
                (s) => s?.date && s?.name && s?.value
              );
              return filled.length > 0
                ? Promise.resolve()
                : Promise.reject(new Error("Add at least one schedule."));
            },
          },
        ]}
      >
        <input type="hidden" />
      </Form.Item>
      <Form.List name="schedule_info" initialValue={[{}]}>
        {(fields, { add, remove }) => {
          const revalidate = () =>
            form.validateFields(["schedule_info_validator"]).catch(() => {});
          return (
            <div className="space-y-3">
              {fields.map(({ key, name }) => (
                <div key={key} className="rounded-xl border p-3 bg-muted/20">
                  <Row gutter={[16, 12]} align="bottom">
                    <Col xs={12} sm={12}>
                      <Form.Item
                        name={[name, "date"]}
                        label="Date"
                        className="mb-0"
                        rules={[{ required: true }]}
                      >
                        <DatePicker
                          disabledDate={disablePastDates}
                          format="MMM D, YYYY"
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={10} sm={8}>
                      <Form.Item
                        name={[name, "value"]}
                        label="Time"
                        className="mb-0"
                        rules={[{ required: true }]}
                      >
                        <TimePicker format="HH:mm" style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col xs={2} sm={2}>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<Trash2 className="h-4 w-4" />}
                        onClick={() => {
                          remove(name);
                          setTimeout(revalidate, 0);
                        }}
                        className="mb-1"
                      />
                    </Col>
                    <Col span={24}>
                      <Form.Item
                        name={[name, "name"]}
                        label="Milestone"
                        className="mb-0"
                        rules={[
                          { required: true, whitespace: true },
                          { validator: textInputValidator },
                        ]}
                      >
                        <Input
                          placeholder="Doors Open"
                          maxLength={50}
                          showCount
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              ))}
              {fields.length < 15 && (
                <Button
                  type="dashed"
                  onClick={() => {
                    add();
                    setTimeout(revalidate, 0);
                  }}
                  block
                  icon={<Plus className="h-4 w-4" />}
                >
                  Add Schedule Item
                </Button>
              )}
            </div>
          );
        }}
      </Form.List>

      {/* Terms & Conditions */}
      <Divider plain>
        <span className="text-sm font-semibold">Terms & Conditions</span>
      </Divider>
      <div className="flex items-center gap-3 mb-2">
        <Switch
          checked={showTC}
          onChange={(checked) => {
            setShowTC(checked);
            if (checked) {
              const existing = form.getFieldValue("tc_sections") ?? [];
              if (existing.length === 0) {
                form.setFieldValue("tc_sections", [{}]);
              }
            }
          }}
          size="small"
        />
        <span className="text-sm text-muted-foreground">
          {showTC
            ? "Hide Terms & Conditions"
            : "Add Terms & Conditions (optional)"}
        </span>
      </div>
      {showTC && (
        <div className="space-y-3 rounded-xl border p-4 bg-muted/20">
          <Form.Item
            name="tc_title"
            label="T&C Title"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "T&C title is required",
              },
              { validator: textInputValidator },
            ]}
          >
            <Input placeholder="Terms & Conditions" maxLength={100} showCount />
          </Form.Item>
          <Form.Item
            name="tc_subtitle"
            label="Subtitle"
            rules={[
              {
                required: true,
                whitespace: true,
                message: "T&C subtitle is required",
              },
              { validator: textInputValidator },
            ]}
          >
            <Input
              placeholder="Please read before purchasing"
              maxLength={100}
              showCount
            />
          </Form.Item>
          <Form.Item
            name="tc_sections_validator"
            rules={[
              {
                validator() {
                  const sections = form.getFieldValue("tc_sections") ?? [];
                  return sections.length > 0
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Add at least one T&C section.")
                      );
                },
              },
            ]}
          >
            <input type="hidden" />
          </Form.Item>
          <Form.List name="tc_sections">
            {(fields, { add, remove }) => {
              const revalidate = () =>
                form.validateFields(["tc_sections_validator"]).catch(() => {});
              return (
                <div className="space-y-3">
                  {fields.map(({ key, name }) => (
                    <div
                      key={key}
                      className="space-y-2 rounded-lg border p-3 bg-background"
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          Section {name + 1} out of 5
                        </span>
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<Trash2 className="h-3 w-3" />}
                          onClick={() => {
                            remove(name);
                            setTimeout(revalidate, 0);
                          }}
                        />
                      </div>
                      <Form.Item
                        name={[name, "heading"]}
                        label="Heading"
                        rules={[
                          { required: true, whitespace: true },
                          { validator: textInputValidator },
                        ]}
                      >
                        <Input
                          placeholder="e.g. Refund Policy"
                          maxLength={100}
                          showCount
                        />
                      </Form.Item>
                      <Form.Item
                        name={[name, "content"]}
                        label="Content"
                        rules={[
                          { required: true, whitespace: true },
                          { validator: textInputValidator },
                        ]}
                      >
                        <Input.TextArea rows={2} maxLength={500} showCount />
                      </Form.Item>
                    </div>
                  ))}
                  {fields.length < 5 && (
                    <Button
                      type="dashed"
                      block
                      onClick={() => {
                        add();
                        setTimeout(revalidate, 0);
                      }}
                      size="small"
                      icon={<Plus className="h-3 w-3" />}
                    >
                      Add Section
                    </Button>
                  )}
                </div>
              );
            }}
          </Form.List>
        </div>
      )}
    </div>
  );
}

export default Step3Content;
